"use client"

import { useState, useTransition } from "react"
import Link from "next/link"
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  Trees,
} from "lucide-react"

/**
 * Client form for /admin/forgot. Posts to /api/users/forgot-password
 * which sends the reset email (subject to Payload's email adapter
 * config). We always return the same success message regardless of
 * whether the email exists, to avoid leaking which addresses are
 * registered admins.
 */
export function ForgotPasswordViewClient() {
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [pending, startTransition] = useTransition()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim()) {
      setError("Veuillez saisir votre adresse email.")
      return
    }
    startTransition(async () => {
      try {
        const res = await fetch("/api/users/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
          credentials: "include",
        })
        // Payload returns 200 with `{ message: "Success" }` even when
        // the email doesn't exist, which is the right behaviour
        // (anti-enumeration). We treat any 2xx as success.
        if (res.status === 429) {
          setError(
            "Trop de demandes — patientez quelques minutes avant de réessayer.",
          )
          return
        }
        if (!res.ok) {
          const j = await res.json().catch(() => ({}))
          setError(
            (j?.errors?.[0]?.message as string) ??
              "Impossible d'envoyer le lien de réinitialisation.",
          )
          return
        }
        setSent(true)
      } catch (err) {
        setError(
          `Connexion impossible — réessayez. (${(err as Error).message})`,
        )
      }
    })
  }

  return (
    <div className="adis-login-page__shell">
      <header className="adis-login-page__brand">
        <span className="adis-login-page__mark" aria-hidden>
          <Trees size={22} strokeWidth={1.7} />
        </span>
        <span className="adis-login-page__brand-text">
          <span className="adis-login-page__brand-title">ADIS 95</span>
          <span className="adis-login-page__brand-sub">
            Mot de passe oublié
          </span>
        </span>
      </header>

      <h1 id="adis-forgot-title" className="adis-login-page__title">
        Réinitialiser votre mot de passe
      </h1>
      <p className="adis-login-page__lede">
        Nous vous enverrons un lien de réinitialisation à l&apos;adresse
        email associée à votre compte administrateur.
      </p>

      {sent ? (
        <div
          role="status"
          className="adis-login-page__error"
          style={{
            background: "rgb(22 163 74 / 0.08)",
            borderColor: "rgb(22 163 74 / 0.3)",
            color: "rgb(15 100 50)",
          }}
        >
          <CheckCircle2 size={16} aria-hidden />
          <span>
            Si un compte est associé à cette adresse, vous recevrez un
            email contenant un lien de réinitialisation valable 1 heure.
          </span>
        </div>
      ) : (
        <form
          className="adis-login-page__form"
          onSubmit={onSubmit}
          noValidate
        >
          {error ? (
            <div role="alert" className="adis-login-page__error">
              <AlertCircle size={16} aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}
          <label className="adis-login-page__field">
            <span className="adis-login-page__field-label">
              <Mail size={14} aria-hidden />
              Adresse email
            </span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@adis95.fr"
              className="adis-login-page__input"
              aria-required="true"
              disabled={pending}
            />
          </label>

          <div className="adis-login-page__actions">
            <Link href="/admin/login" className="adis-login-page__forgot">
              Retour à la connexion
            </Link>
            <button
              type="submit"
              className="adis-login-page__submit"
              disabled={pending}
            >
              {pending ? (
                <>
                  <Loader2
                    size={14}
                    aria-hidden
                    className="adis-login-page__spin"
                  />
                  Envoi en cours
                </>
              ) : (
                <>
                  <KeyRound size={14} aria-hidden />
                  Envoyer le lien
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
